import type { BillingProductKey } from '../../supabase/functions/_shared/billingPolicy';
import { WEB_PURCHASE_MARK_KEY } from '../native/webPurchasesAdapter';
import { billingStore, noteWebPurchaseConfirmed, noteWebPurchaseReturn, refreshBilling, type BillingState } from './billingStore';

// Coming back from RevenueCat's hosted payment page (web version, item 39). RevenueCat sends the browser to the
// web app's address with `?purchase=done` (docs/web/WEB-BILLING-SETUP.md). Before leaving, the purchase lane
// noted which product was being bought and what the account looked like (sessionStorage); on return the app
// strips the parameters from the address, says "being confirmed", and asks the server a few times until the
// purchase shows (RevenueCat may need a few seconds). It never claims a purchase the server has not seen.
interface CheckoutMark {
  product: BillingProductKey;
  at: number;
  userId: string;
  signature: string;
}

let returned: { mark: CheckoutMark | null } | null = null;

export function accountSignatureOf(state: BillingState): string {
  return [state.plan ?? '', state.activeUntil ?? '', state.server?.singlePurchases ?? 0, state.server?.addOnRemainingUnits ?? 0, state.server?.poolUnits ?? 0].join(':');
}

export function rememberWebCheckout(product: BillingProductKey, userId: string): void {
  try {
    const mark: CheckoutMark = { product, at: Date.now(), userId, signature: accountSignatureOf(billingStore.get()) };
    window.sessionStorage.setItem(WEB_PURCHASE_MARK_KEY, JSON.stringify(mark));
  } catch {
    // a private window without storage still buys; the return just says less
  }
}

function readMark(): CheckoutMark | null {
  try {
    const raw = window.sessionStorage.getItem(WEB_PURCHASE_MARK_KEY);
    window.sessionStorage.removeItem(WEB_PURCHASE_MARK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CheckoutMark>;
    if (typeof parsed.product !== 'string' || typeof parsed.userId !== 'string' || typeof parsed.signature !== 'string') return null;
    return { product: parsed.product as BillingProductKey, at: Number(parsed.at) || 0, userId: parsed.userId, signature: parsed.signature };
  } catch {
    return null;
  }
}

// At boot, before anything else reads the address: was this page opened by the payment page's redirect?
export function captureWebPurchaseReturn(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('purchase') !== 'done') return false;
  returned = { mark: readMark() };
  // The address goes back to plain: a reload or a bookmark must not replay the return.
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
  } catch {
    // nothing to clean
  }
  return true;
}

export function pendingWebPurchaseReturn(): boolean {
  return returned !== null;
}

// Once signed in: poll the server until the purchase shows, or give up quietly after about half a minute (the
// plan page keeps saying it is being confirmed; the next refresh shows it).
export async function resumeWebPurchaseReturn(options: { tries?: number; waitMs?: number; onConfirmed?: (product: BillingProductKey) => void } = {}): Promise<'confirmed' | 'notYet' | 'none'> {
  if (!returned) return 'none';
  const { mark } = returned;
  returned = null;
  noteWebPurchaseReturn();
  const tries = options.tries ?? 8;
  const waitMs = options.waitMs ?? 4_000;
  for (let i = 0; i < tries; i += 1) {
    await refreshBilling();
    const state = billingStore.get();
    if (mark && state.userId === mark.userId && accountSignatureOf(state) !== mark.signature) {
      noteWebPurchaseConfirmed(mark.product);
      options.onConfirmed?.(mark.product);
      return 'confirmed';
    }
    if (i < tries - 1) await new Promise((resolve) => setTimeout(resolve, waitMs));
  }
  return 'notYet';
}

export function resetWebPurchaseReturnForTests(): void {
  returned = null;
}
